import React, { useState, useEffect, useMemo } from "react";
import { Card, Button, Row, Col, Form, Dropdown } from "react-bootstrap";
import ReactCountryFlag from "react-country-flag";
import getCountryISO2 from "country-iso-3-to-2";
import { useLocation, useNavigate } from "react-router-dom";
import { createTeam, updateTeam } from "../../../services/teamService";
import { FaRegHandPointRight, FaGamepad } from "react-icons/fa6";
import Prizes from "../../Fantasy/Components/Prizes";
import { updateLeaderbordById } from "../../../services/leaderboardService";
import { IoWarning } from "react-icons/io5";
import {
	CreateTeamInput,
	EditTeamRider,
	Rider,
	RiderInTeam,
	TeamWithRider,
} from "../../../types/model";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { setShowLoginModal } from "../../../store/slices/userSlice";
import { GameModeAssignemts, Gender } from "../../../constants/constants";
import PickAndRankTeamRiderCard from "./PickAndRank/PickAndRankTeamRiderCard";
import { FaSave } from "react-icons/fa";
import PickAndRankModeRulesCard from "./PickAndRankModeRulesCard";
import WarningModal from "../../common/components/WarningModal";
import EligibleRidersSection from "./EligibleRidersSection";
import PickAndRankChoiceRiders from "./PickAndRank/PickAndRankChoiceRiders";

interface PickAndRankModeProps {
	numberOfMen: number;
	numberOfWomen: number;
	eligibleRiders: Rider[];
	team: TeamWithRider;
	globalLeague: any;
	disable: boolean;
}

const PickAndRankMode = ({
	numberOfMen,
	numberOfWomen,
	eligibleRiders,
	team,
	globalLeague,
	disable,
}: PickAndRankModeProps) => {
	console.log("Team", team);

	const navigate = useNavigate();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const sport = searchParams.get("sport");
	const series = searchParams.get("series");
	const leagueId = searchParams.get("leagueId");

	// local state
	const [riders, setRiders] = useState<EditTeamRider[]>([]);
	const [showDisabledModal, setShowDisabledModal] = useState(false);
	const [showIneligibleModal, setShowIneligibleModal] = useState(false);
	const [ineligibleReasons, setIneligibleReasons] = useState<string[]>([]);
	const [selectedBestTrickRiderMen, setSelectedBestTrickRiderMen] = useState<
		string | undefined
	>(team?.bestTrickRiderMen);
	const [selectedPeoplesChoiceRiderMen, setSelectedPeoplesChoiceRiderMen] =
		useState<string | undefined>(team?.PeoplesChoiceRiderMen);
	const [selectedBestTrickRiderWomen, setSelectedBestTrickRiderWomen] =
		useState<string | undefined>(team?.bestTrickRiderWomen);
	const [selectedPeoplesChoiceRiderWomen, setSelectedPeoplesChoiceRiderWomen] =
		useState<string | undefined>(team?.PeoplesChoiceRiderWomen);
	const [editTeamView, setEditTeamView] = useState<"default" | "columns">(
		"default",
	);

	//Redux
	const { isLoggedIn } = useAppSelector((state: RootState) => state.user);

	//constants
	const dispatch = useAppDispatch();
	const maleRiders = riders.filter(
		(rider: any) => rider.rider.gender === Gender.male,
	);

	const allMaleRiders = eligibleRiders.filter(
		(rider) => rider.gender === Gender.male,
	);

	const femaleRiders = riders.filter(
		(rider) => rider.rider.gender === Gender.female,
	);

	const allFemaleRiders = eligibleRiders.filter(
		(rider) => rider.gender === Gender.female,
	);

	const uniqueCountries = useMemo(() => {
		const countrySet = new Set(
			eligibleRiders.map((rider: Rider) =>
				(rider.country ?? "").toUpperCase().trim(),
			),
		);
		return Array.from(countrySet).sort();
	}, [eligibleRiders]);

	console.log("team riders", team);

	// set initial riders from team prop
	useEffect(() => {
		if (team && Array.isArray(team.riders) && team.riders.length > 0) {
			setRiders(team.riders);

			if (team.bestTrickRiderMen) {
				setSelectedBestTrickRiderMen(team.bestTrickRiderMen);
			}
			if (team.PeoplesChoiceRiderMen) {
				setSelectedPeoplesChoiceRiderMen(team.PeoplesChoiceRiderMen);
			}
			if (team.bestTrickRiderWomen) {
				setSelectedBestTrickRiderWomen(team.bestTrickRiderWomen);
			}
			if (team.PeoplesChoiceRiderWomen) {
				setSelectedPeoplesChoiceRiderWomen(team.PeoplesChoiceRiderWomen);
			}
		}
	}, [team, eligibleRiders]);

	// Finds an empty rank for a new rider
	const findRiderRank = (gender: Gender) => {
		const takenRanks = new Set(
			riders
				.filter((rider) => rider.rider.gender === gender)
				.map((rider) => rider.rank),
		);
		for (let i = 1; i <= 10; i++) {
			if (!takenRanks.has(i)) {
				return i;
			}
		}
		return 0;
	};

	const addRiderToTeam = (rider: Rider) => {
		if (
			!riders.some((teamRider) => teamRider.rider._id === rider._id) &&
			riders.length < numberOfMen + numberOfWomen
		) {
			setRiders([
				...riders,
				{ rider: rider, rank: findRiderRank(rider.gender as Gender) },
			]);
		}
	};

	const removeRiderFromTeam = (riderId: string) => {
		setRiders(riders.filter((rider) => rider.rider._id !== riderId));
	};

	const handleSaveTeam = async () => {
		if (!isLoggedIn) {
			dispatch(setShowLoginModal(true));
			return;
		}

		const reasons = getIneligibleReasons();
		if (reasons.length > 0) {
			setIneligibleReasons(reasons);
			setShowIneligibleModal(true);
			return;
		}

		try {
			if (team?._id && team?._id !== "") {
				// Update existing team
				const teamData: CreateTeamInput = {
					...team,
					riders: riders,
					selectedTeam: "",
					bestTrickRiderMen: selectedBestTrickRiderMen,
					PeoplesChoiceRiderMen: selectedPeoplesChoiceRiderMen,
					bestTrickRiderWomen: selectedBestTrickRiderWomen,
					PeoplesChoiceRiderWomen: selectedPeoplesChoiceRiderWomen,
				};

				const res = await updateTeam(team._id, teamData);
				if (res) {
					if (leagueId) {
						navigate(`/league/${leagueId}`);
					} else {
						navigate("/fantasy");
					}
				} else {
					// Handle update failure
				}
			} else {
				// Create new team
				const teamData: CreateTeamInput = {
					league: leagueId ? leagueId : null,
					riders: riders,
					name: team?.name ? team.name : "",
					sport: sport ?? "",
					series: series ?? "",
					selectedTeam: "",
					bestTrickRiderMen: selectedBestTrickRiderMen,
					PeoplesChoiceRiderMen: selectedPeoplesChoiceRiderMen,
					bestTrickRiderWomen: selectedBestTrickRiderWomen,
					PeoplesChoiceRiderWomen: selectedPeoplesChoiceRiderWomen,
				};

				const res = await createTeam(teamData);
				if (res) {
					if (leagueId) {
						navigate(`/league/${leagueId}`);
					} else {
						navigate("/fantasy");
					}
				}
			}
		} catch (error) {
			console.error("Error saving team:", error);
		}
	};

	const updateRiderRank = (riderId: string, newRank: number) => {
		let newRiders = riders.map((rider) =>
			rider.rider._id === riderId ? { ...rider, rank: newRank } : rider,
		);

		// Sort and re-rank to avoid duplicates
		newRiders.sort((a, b) => a.rank - b.rank);
		setRiders(newRiders);
	};

	const isAddDisabled = (gender: Gender, rider: Rider) => {
		const isAlreadyInTeam = riders.some(
			(teamRider) => teamRider.rider._id === rider._id,
		);
		if (isAlreadyInTeam) return true;

		return gender === "m"
			? riders.filter((r) => r.rider.gender === "m").length >= numberOfMen
			: riders.filter((r) => r.rider.gender === "f").length >= numberOfWomen;
	};

	// Check if any two riders of the same gender have the same rank

	const hasDuplicateRank = (riders: RiderInTeam[]) => {
		return riders.some((rider, index) => {
			const duplicateIndex = riders.findIndex((r, i) => {
				return r.rank === rider.rank && i !== index;
			});
			return duplicateIndex !== -1;
		});
	};

	const getIneligibleReasons = () => {
		const reasons: string[] = [];
		const menCount = maleRiders.length;
		const womenCount = femaleRiders.length;

		if (menCount < numberOfMen) {
			const remaining = numberOfMen - menCount;
			reasons.push(
				`Add ${remaining} more male rider${remaining === 1 ? "" : "s"}.`,
			);
		} else if (menCount > numberOfMen) {
			const extra = menCount - numberOfMen;
			reasons.push(`Remove ${extra} extra male rider${extra === 1 ? "" : "s"}.`);
		}

		if (womenCount < numberOfWomen) {
			const remaining = numberOfWomen - womenCount;
			reasons.push(
				`Add ${remaining} more female rider${remaining === 1 ? "" : "s"}.`,
			);
		} else if (womenCount > numberOfWomen) {
			const extra = womenCount - numberOfWomen;
			reasons.push(
				`Remove ${extra} extra female rider${extra === 1 ? "" : "s"}.`,
			);
		}

		if (hasDuplicateRank(maleRiders)) {
			reasons.push("Resolve duplicate rank values for male riders.");
		}

		if (hasDuplicateRank(femaleRiders)) {
			reasons.push("Resolve duplicate rank values for female riders.");
		}

		return reasons;
	};

	const isTeamEligible = getIneligibleReasons().length === 0;

	const handleAttemptSave = (ignoreDisable = false) => {
		if (disable && !ignoreDisable) {
			setShowDisabledModal(true);
			return;
		}

		const reasons = getIneligibleReasons();
		if (reasons.length > 0) {
			setIneligibleReasons(reasons);
			setShowIneligibleModal(true);
			return;
		}

		handleSaveTeam();
	};

	const renderTeamRiders = (_riders: EditTeamRider[], gender: Gender) => {
		return (
			Array.isArray(_riders) &&
			// First, map over the team riders to include full rider details from eligibleRiders
			_riders
				.map((teamRider: EditTeamRider) => ({
					...teamRider,
					...eligibleRiders.find(
						(rider) => rider?._id === teamRider?.rider?._id,
					),
				}))
				// Sort by rank
				.sort((a, b) => a.rank - b.rank)
				.map((rider: EditTeamRider, index) => (
					<PickAndRankTeamRiderCard
						rider={rider}
						gender={gender}
						numberOfMen={numberOfMen}
						numberOfWomen={numberOfWomen}
						updateRiderRank={updateRiderRank}
						removeRiderFromTeam={removeRiderFromTeam}
						getCountryISO2={getCountryISO2}
					/>
				))
		);
	};

	const myTeamCard = (
		<Card className="mb-3 bg-secondary text-light border-0">
			<Card.Header className="bg-secondary text-white fs-2 fw-bold">
				My Team
			</Card.Header>
			<Card.Body>
				<div className="ms-4 mb-1 fw-bold">
					Men:
					<span className="text-white fw-light" style={{ fontSize: "0.8rem" }}>
						{" "}
						{numberOfMen - maleRiders.length} Remaining
					</span>
				</div>
				{renderTeamRiders(maleRiders, Gender.male)}
				<PickAndRankChoiceRiders
					allRiders={allMaleRiders}
					selectedBestTrickRider={selectedBestTrickRiderMen}
					setSelectedBestTrickRider={setSelectedBestTrickRiderMen}
					selectedPeoplesChoiceRider={selectedPeoplesChoiceRiderMen}
					setSelectedPeoplesChoiceRider={setSelectedPeoplesChoiceRiderMen}
					labelSuffix="Men"
				/>

				<div className="ms-4 mt-3 mb-1 fw-bold">
					Women:
					<span className="text-white fw-light" style={{ fontSize: "0.8rem" }}>
						{" "}
						{numberOfWomen - femaleRiders.length} Remaining
					</span>
				</div>
				{renderTeamRiders(femaleRiders, Gender.female)}
				<PickAndRankChoiceRiders
					allRiders={allFemaleRiders}
					selectedBestTrickRider={selectedBestTrickRiderWomen}
					setSelectedBestTrickRider={setSelectedBestTrickRiderWomen}
					selectedPeoplesChoiceRider={selectedPeoplesChoiceRiderWomen}
					setSelectedPeoplesChoiceRider={setSelectedPeoplesChoiceRiderWomen}
					labelSuffix="Women"
				/>
			</Card.Body>
			<Card.Footer>
				<Row>
					<Col className="w-100 text-end">
						<Button
							onClick={() => handleAttemptSave()}
							variant={isTeamEligible ? "primary" : "secondary"}
							className="px-4 text-white"
						>
							<FaSave className="me-2 mb-1" />
							Save Team
						</Button>
					</Col>
				</Row>
			</Card.Footer>
		</Card>
	);

	const eligibleRidersCard = (
		<EligibleRidersSection
			uniqueCountries={uniqueCountries}
			eligibleRiders={eligibleRiders}
			isAddDisabled={isAddDisabled}
			addRiderToTeam={addRiderToTeam}
			sport={sport ?? undefined}
			gameModeAssignment={GameModeAssignemts.PickAndRankHardline}
		/>
	);

	return (
		<div>
			<Prizes leagues={[globalLeague]} />
			<PickAndRankModeRulesCard
				numberOfMen={numberOfMen}
				numberOfWomen={numberOfWomen}
			/>
			<Row className="mb-3 d-none d-md-flex">
				<Col className="d-flex justify-content-md-end justify-content-center">
					<div className="btn-group" role="group" aria-label="Edit team view toggle">
						<Button
							variant={editTeamView === "default" ? "primary" : "outline-light"}
							onClick={() => setEditTeamView("default")}
						>
							Standard View
						</Button>
						<Button
							variant={editTeamView === "columns" ? "primary" : "outline-light"}
							onClick={() => setEditTeamView("columns")}
						>
							Column View
						</Button>
					</div>
				</Col>
			</Row>

			{editTeamView === "columns" ? (
				<Row className="g-3">
					<Col xl={7}>{eligibleRidersCard}</Col>
					<Col xl={5}>{myTeamCard}</Col>
				</Row>
			) : (
				<>
					{myTeamCard}
					{eligibleRidersCard}
				</>
			)}

			{/* Warning Modal for disabled state */}
			<WarningModal
				show={showDisabledModal}
				onHide={() => setShowDisabledModal(false)}
				title="Event Underway"
				body={
					<div>
						<p>An event is currently underway.</p>
						<p>
							Changes to your team will no longer be reflected in the current
							event.
						</p>
					</div>
				}
				footer={
					<Button
						variant="primary"
						onClick={() => {
							handleAttemptSave(true);
						}}
					>
						Save Team
					</Button>
				}
			/>
			<WarningModal
				show={showIneligibleModal}
				onHide={() => setShowIneligibleModal(false)}
				title="Team Ineligible"
				body={
					<div>
						<p>Complete the following before saving your team:</p>
						<ul className="mb-0">
							{ineligibleReasons.map((reason) => (
								<li key={reason}>{reason}</li>
							))}
						</ul>
					</div>
				}
			/>
		</div>
	);
};

export default PickAndRankMode;
